import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';

import { useTheme } from '@mui/material/styles';

// Components
import { getRouteDisplayName } from '@common/Util.jsx';
import AppContainer from '@components/AppContainer/AppContainer';
import ScrollToTop from '@components/ScrollToTop/ScrollToTop.jsx';
import { useGlobalContext } from '@hooks/Context/GlobalContext';
import AllNews from '@pages/AllNews/AllNews';
import BookingConfirmation from '@pages/BookingConfirmation/BookingConfirmation';
import BookingProcess from '@pages/BookingProcess/BookingProcess';
import Catalog from '@pages/Catalog/Catalog';
import EventDetails from '@pages/EventDetails/EventDetails.jsx';
import GeneralInformation from '@pages/GeneralInformation/GeneralInformation';
import Home from '@pages/Home/Home';
import LegalNotice from '@pages/LegalNotice/LegalNotice';
import LogIn from '@pages/LogIn/LogIn.jsx';
import MyProfile from '@pages/MyProfile/MyProfile';
import NotFound from '@pages/NotFound/NotFound';
import ParticipantConfirmation from '@pages/ParticipantConfirmation/ParticipantConfirmation';
import PrivacyPolicy from '@pages/PrivacyPolicy/PrivacyPolicy';

import * as constants from './Constants';

const PublicRoute = ({ element: Element, hasHomePage, ...rest }) => {
  const [contextData] = useGlobalContext();
  const theme = useTheme();

  return contextData.user ? (
    <Navigate
      to={constants.routes.path.myarea}
      state={{
        from: getRouteDisplayName(constants.routes.path.login),
        path: constants.routes.path.login
      }}
      replace
    />
  ) : (
    <>
      <ScrollToTop />
      <AppContainer
        rest={rest}
        Component={Element}
        headerConfig={{ ...theme?.header, hasHomePage: hasHomePage }}
      />
    </>
  );
};

const PrivateRoute = ({ element: Element, hasHomePage, ...rest }) => {
  const [contextData] = useGlobalContext();
  const theme = useTheme();

  return !contextData.user ? (
    <Navigate
      to={constants.routes.path.login}
      state={{
        from: getRouteDisplayName(rest.path),
        path: rest.path
      }}
      replace
    />
  ) : (
    <>
      <ScrollToTop />
      <AppContainer
        rest={rest}
        Component={Element}
        headerConfig={{ ...theme?.header, hasHomePage: hasHomePage }}
      />
    </>
  );
};

const PublicAndPrivateRoute = ({ element: Element, hasHomePage, ...rest }) => {
  const theme = useTheme();

  return (
    <>
      <ScrollToTop />
      <AppContainer
        rest={rest}
        Component={Element}
        headerConfig={{ ...theme?.header, hasHomePage: hasHomePage }}
      />
    </>
  );
};

const AppRouter = ({ hasHomePage }) => {
  const SYS_NAME = import.meta.env.VITE_SYSTEM_NAME;
  const theme = useTheme();
  useEffect(() => {
    if (theme?.tabName) {
      if (SYS_NAME !== '') {
        document.title = `(${SYS_NAME}) ${theme?.tabName}`;
      } else {
        document.title = theme.tabName;
      }
    }
    if (theme.favicon) {
      const link = document.querySelector('link[rel~="icon"]') || document.createElement('link');
      link.rel = 'icon';
      link.href = theme?.favicon;
      document.head.appendChild(link);
    }
  }, []);

  return (
    <BrowserRouter
      future={{
        v7_startTransition: true,
        v7_relativeSplatPath: true
      }}
      data-testid={'appRouter'}
    >
      <Routes>
        <Route
          path={constants.routes.path.home + '/*'}
          element={
            theme?.haveLogin ? (
              <PublicRoute
                path={constants.routes.path.home}
                element={LogIn}
                hasHomePage={hasHomePage}
              />
            ) : (
              <Navigate to={constants.routes.path.catalog} replace />
            )
          }
        />
        <Route
          path={constants.routes.path.home}
          element={
            theme?.haveLogin ? (
              <PublicRoute
                path={constants.routes.path.home}
                element={LogIn}
                hasHomePage={hasHomePage}
              />
            ) : hasHomePage ? (
              <PublicRoute
                path={constants.routes.path.home}
                element={Home}
                hasHomePage={hasHomePage}
              />
            ) : (
              <Navigate to={constants.routes.path.catalog} replace />
            )
          }
        />

        <Route
          path={constants.routes.path.catalog}
          element={
            <PublicAndPrivateRoute
              path={constants.routes.path.catalog}
              element={Catalog}
              hasHomePage={hasHomePage}
            />
          }
        />
        <Route
          path={constants.routes.path.allNews}
          element={
            <PublicAndPrivateRoute
              path={constants.routes.path.allNews}
              element={AllNews}
              hasHomePage={hasHomePage}
            />
          }
        />
        <Route
          path={constants.routes.path.eventDetails}
          element={
            <PublicAndPrivateRoute
              path={constants.routes.path.eventDetails}
              element={EventDetails}
              hasHomePage={hasHomePage}
            />
          }
        />

        <Route
          path={constants.routes.path.eventBooking}
          element={
            <PublicAndPrivateRoute
              path={constants.routes.path.eventBooking}
              element={BookingProcess}
              hasHomePage={hasHomePage}
            />
          }
        />
        <Route
          path={constants.routes.path.legalNotice}
          element={
            <PublicAndPrivateRoute
              path={constants.routes.path.legalNotice}
              element={LegalNotice}
              hasHomePage={hasHomePage}
            />
          }
        />
        <Route
          path={constants.routes.path.privacyPolicy}
          element={
            <PublicAndPrivateRoute
              path={constants.routes.path.privacyPolicy}
              element={PrivacyPolicy}
              hasHomePage={hasHomePage}
            />
          }
        />
        <Route
          path={constants.routes.path.bookingConfirmation}
          element={
            <PublicAndPrivateRoute
              path={constants.routes.path.bookingConfirmation}
              element={BookingConfirmation}
              hasHomePage={hasHomePage}
            />
          }
        />
        <Route
          path={constants.routes.path.participantConfirmation}
          element={
            <PublicAndPrivateRoute
              path={constants.routes.path.participantConfirmation}
              element={ParticipantConfirmation}
              hasHomePage={hasHomePage}
            />
          }
        />

        <Route
          path={constants.routes.path.generalinfo}
          element={<PublicRoute element={GeneralInformation} hasHomePage={hasHomePage} />}
        />
        <Route
          path={constants.routes.path.login + '/*'}
          element={
            theme?.haveLogin ? (
              <PublicRoute
                path={constants.routes.path.home}
                element={LogIn}
                hasHomePage={hasHomePage}
              />
            ) : (
              <Navigate to={constants.routes.path.catalog} replace />
            )
          }
        />
        <Route
          path={constants.routes.path.mailLogin + '/*'}
          element={
            theme?.haveLogin ? (
              <PublicRoute
                path={constants.routes.path.home}
                element={LogIn}
                hasHomePage={hasHomePage}
              />
            ) : (
              <Navigate to={constants.routes.path.catalog} replace />
            )
          }
        />
        <Route
          path={constants.routes.path.mailConfirmation + '/*'}
          element={
            theme?.haveLogin ? (
              <PublicRoute
                path={constants.routes.path.home}
                element={LogIn}
                hasHomePage={hasHomePage}
              />
            ) : (
              <Navigate to={constants.routes.path.catalog} replace />
            )
          }
        />
        <Route
          path={constants.routes.path.myProfile}
          element={
            <PrivateRoute
              path={constants.routes.path.myProfile}
              element={MyProfile}
              hasHomePage={hasHomePage}
            />
          }
        />
        <Route
          path={constants.routes.path.notFound}
          element={
            <PublicAndPrivateRoute
              path={constants.routes.path.notFound}
              element={NotFound}
              hasHomePage={hasHomePage}
            />
          }
        />
        <Route path='*' element={<Navigate to={constants.routes.path.notFound} replace />} />
      </Routes>
    </BrowserRouter>
  );
};
export default AppRouter;
